import { useEffect, useState } from 'react';
import { subscribeToMessages, getUnreadMessageCount } from '../lib/supabase/messages';
import messageSound from '../lib/soundUtils/messageSound';

/**
 * Custom hook to manage message notifications and unread count
 * This hook should be used at the top level (Navbar) to ensure it's always active
 */
export const useMessageNotifications = (userId) => {
    const [unreadCount, setUnreadCount] = useState(0);
    const [isMuted, setIsMuted] = useState(false);
    const [volume, setVolume] = useState(0.7);

    // Load saved settings
    useEffect(() => {
        const savedVolume = localStorage.getItem('messageVolume');
        const savedMuted = localStorage.getItem('messageMuted');
        if (savedVolume) setVolume(parseFloat(savedVolume));
        if (savedMuted) setIsMuted(savedMuted === 'true');
    }, []);

    // Fetch initial count and set up subscription
    useEffect(() => {
        if (!userId) return;

        console.log('[useMessageNotifications] Setting up for user:', userId);

        // Fetch initial count
        const fetchCount = async () => {
            const count = await getUnreadMessageCount(userId);
            console.log('[useMessageNotifications] Initial count:', count);
            setUnreadCount(count);
        };

        fetchCount();

        // Subscribe to new messages
        const subscription = subscribeToMessages(userId, async (newMessage) => {
            console.log('[useMessageNotifications] New message received, updating badge and playing sound');

            // Immediately fetch and update count
            const count = await getUnreadMessageCount(userId);
            console.log('[useMessageNotifications] Updated count:', count);
            setUnreadCount(count);

            // Play sound
            if (!isMuted) {
                console.log('[useMessageNotifications] Playing sound');
                messageSound.play(volume);
            }
        });

        // Poll every 30 seconds as fallback
        const interval = setInterval(async () => {
            const count = await getUnreadMessageCount(userId);
            setUnreadCount(count);
        }, 30000);

        return () => {
            console.log('[useMessageNotifications] Cleaning up');
            if (subscription) {
                subscription.unsubscribe();
            }
            clearInterval(interval);
        };
    }, [userId, isMuted, volume]);

    // Update count when settings change
    const updateSettings = (newMuted, newVolume) => {
        if (newMuted !== undefined) setIsMuted(newMuted);
        if (newVolume !== undefined) setVolume(newVolume);
    };

    // Manual refresh function
    const refreshCount = async () => {
        if (!userId) return;
        const count = await getUnreadMessageCount(userId);
        setUnreadCount(count);
    };

    return {
        unreadCount,
        refreshCount,
        updateSettings
    };
};
